import { Address, Site, SitesApi } from '@/api';
import { ApiUtils } from '@/common/ApiUtils';
import { AddressService } from '@/services/address-service';
import { AddressExtended, SiteExtended } from '@/models/extensions';

export class SiteService {
	constructor(
		private siteApi: SitesApi = new SitesApi(),
		private addressService: AddressService = new AddressService(),
	) {}

	public async getSiteById<T extends Site>(
		id: string | number,
		includes: string,
	): Promise<{ site?: T; errorMessage?: string }> {
		if (typeof id === 'string') id = parseInt(id, 10);
		try {
			const response = await this.siteApi.getSiteById(id, {
				params: {
					include: includes,
				},
			});

			const site = <T>response.data;

			if (includes.includes('address')) {
				const extendedSite = <SiteExtended<AddressExtended>>site;
				extendedSite.address = this.addressService.convertAddressToObject(
					<Address>extendedSite.address,
				);
				return { site: <T>extendedSite };
			}

			return { site };
		} catch (e) {
			console.error('Error getting site by ID:', e);
			return ApiUtils.handleApiError(e, `Failed to fetch site by ID: ${id}`);
		}
	}

	public async updateSite(
		site: SiteExtended<AddressExtended>,
		updatePhysicalLocation: boolean = false,
	): Promise<{ success?: boolean; errorMessage?: string }> {
		if (site?.id == null)
			return { success: false, errorMessage: 'Failed to update site, no ID provided' };
		try {
			if (typeof site.address?.rawAddress?.geometry?.location?.lat === 'function')
				site.address.rawAddress.geometry.location.lat = site.address.rawAddress.geometry.location.lat();
			if (typeof site.address?.rawAddress?.geometry?.location?.lng === 'function')
				site.address.rawAddress.geometry.location.lng = site.address.rawAddress.geometry.location.lng();

			const { data }: any = await this.siteApi.updateSite(site.id, {
				data: {
					id: site.id,
					orgId: site.orgId,
					name: site.name,
					latitude: updatePhysicalLocation
						? <string>site.address?.rawAddress?.geometry?.location?.lat
						: site.latitude,
					longitude: updatePhysicalLocation
						? <string>site.address?.rawAddress?.geometry?.location?.lng
						: site.longitude,
				},
			});
			site.latitude = data?.latitude ?? site.latitude;
			site.longitude = data?.longitude ?? site.longitude;
			return { success: true };
		} catch (e) {
			console.error('Error updating site:', e);
			return ApiUtils.handleApiError(e, 'Failed to update site');
		}
	}

	public async deleteSite(
		id: string | number,
	): Promise<{ success?: boolean; errorMessage?: string }> {
		if (typeof id === 'string') id = parseInt(id, 10);
		try {
			await this.siteApi.deleteSite(id);
			return { success: true };
		} catch (e) {
			console.error('Error deleting site:', e);
			return ApiUtils.handleApiError(e, 'Failed to delete site');
		}
	}
}
